// apps/web/src/features/auth/RequireLogin.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import { me, logout, consentStatus, CONSENT_APP_ID } from "@/lib/api";
import { COLOR, FW } from "@/ui/tokens";

const BASE = {
    W: 1264,
    MARGIN_X: 108,
    TOP_GAP: 220,

    TITLE_FZ: 44,
    DESC_FZ: 26,

    BTN_W: 724,
    BTN_H: 124,
    BTN_FZ: 40,
} as const;

function useScale() {
    const { s, px, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    return { s, spx: px, sfpx };
}

export default function RequireLogin({ children, featureName = "마이페이지" }: { children: React.ReactNode; featureName?: string }) {
    const nav = useNavigate();
    const loc = useLocation();
    const { s, spx, sfpx } = useScale();

    const [state, setState] = useState<"checking" | "ok" | "need_login">("checking");

    useEffect(() => {
        const access = localStorage.getItem("bt.access");
        if (!access) {
            setState("need_login");
            return;
        }
        // 토큰이 있으면 유효성 검증
        (async () => {
            try {
                await me();
            } catch (e: any) {
                if (e?.status === 401) {
                    logout(); // 무효한 토큰 정리
                    setState("need_login");
                } else {
                    // 네트워크 에러 등은 일단 통과
                    setState("ok");
                }
                return;
            }

            // 로그인은 유효하지만 이 서비스 약관 동의가 없는 경우.
            // (로그인 직후 동의 화면을 닫고 앱을 다시 연 경로가 여기로 온다)
            //
            // 조회가 실패하면 통과시킨다. 동의 게이트 때문에 서비스 전체가
            // 막히는 쪽이, 게이트가 한 번 새는 쪽보다 나쁘다.
            try {
                const st = await consentStatus(CONSENT_APP_ID);
                if (st?.required) {
                    nav("/auth/agree", {
                        replace: true,
                        state: { mode: "consent", appId: st.app_id || CONSENT_APP_ID, missing: st.missing || [] },
                    });
                    return;
                }
            } catch {}

            setState("ok");
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (state === "checking") {
        return <div style={{ padding: 24, textAlign: "center" }}>확인 중...</div>;
    }

    if (state === "ok") return <>{children}</>;

    return (
        <PageShell
            title={featureName}
            onBack={() => nav(-1)}
            backIcon={withBase("icons/prev1.svg")}
            width={spx(BASE.W)}
            bodyStyle={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}
        >
            <div style={{ height: spx(BASE.TOP_GAP) }} />
            <div style={{ fontSize: sfpx(BASE.TITLE_FZ), fontWeight: FW.extraBold, color: COLOR.text }}>
                로그인이 필요합니다
            </div>
            <div style={{ height: spx(18) }} />
            <div style={{ fontSize: sfpx(BASE.DESC_FZ), fontWeight: 700, color: "#444", lineHeight: 1.35 }}>
                {featureName} 기능을 사용하려면 로그인 후 이용해주세요.
            </div>

            <div style={{ height: spx(80) }} />
            <div style={{ display: "grid", placeItems: "center" }}>
                <button
                    type="button"
                    onClick={() => nav("/auth/login", { replace: true, state: { from: loc.pathname } })}
                    style={{
                        width: spx(BASE.BTN_W),
                        height: spx(BASE.BTN_H),
                        borderRadius: s(12),
                        background: COLOR.black,
                        color: COLOR.white,
                        fontSize: sfpx(BASE.BTN_FZ),
                        fontWeight: FW.extraBold,
                        border: "none",
                    }}
                >
                    확인
                </button>
            </div>
        </PageShell>
    );
}
